<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('borrowingSparePart.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('borrowingSparePart.pageTitle'),
            href: '/admin/borrowing-spare-parts'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div
            class="ts-border ts-border-gray-300 ts-rounded-lg ts-p-3 ts-mb-3"
          >
            <form class="form-horizontal ts-space-y-2">
              <div class="row">
                <div class="col-md-6 ts-space-y-3">
                  <customer
                    v-model="model"
                    :validate="errors"
                    @loading="value => (loading = value)"
                  />
                </div>
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label"
                      :class="{ required: isRequired }"
                      >{{ $t('borrowingSparePart.project') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.project_id"
                        class="ts-w-full"
                        :options="projectList"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                      ></a-select>
                      <div class="validate" v-if="errors.has('project_id')">
                        {{ errors.first('project_id') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('borrowingSparePart.warehouse') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.location_id"
                        class="ts-w-full"
                        :options="locationList"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        @change="clearProductOnChangeLocation"
                      ></a-select>
                      <div class="validate" v-if="errors.has('location_id')">
                        {{ errors.first('location_id') }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('borrowingSparePart.serviceDate') }}</label
                    >
                    <div class="col-md-7">
                      <a-date-picker
                        v-model="model.service_date"
                        format="DD-MM-YYYY"
                        valueFormat="DD-MM-YYYY"
                        class="ts-w-full"
                      ></a-date-picker>
                      <div class="validate" v-if="errors.has('service_date')">
                        {{ errors.first('service_date') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('borrowingSparePart.serviceType') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.service_type_id"
                        class="ts-w-full"
                        :options="serviceType"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                      ></a-select>
                      <div
                        class="validate"
                        v-if="errors.has('service_type_id')"
                      >
                        {{ errors.first('service_type_id') }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="row">
                    <label class="col-md-3 form-col-label control-label">{{
                      $t('borrowingSparePart.plateNumber')
                    }}</label>
                    <div class="col-md-7">
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="model.plate_number"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('borrowingSparePart.requestBy') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.requested_by"
                        class="ts-w-full"
                        :options="employeeList"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                      ></a-select>
                      <div class="validate" v-if="errors.has('requested_by')">
                        {{ errors.first('requested_by') }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label"
                      :class="{ required: isSale }"
                      >{{ $t('borrowingSparePart.deliveredBy') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.delivered_by"
                        class="ts-w-full"
                        :options="employeeList"
                      ></a-select>
                      <div class="validate" v-if="errors.has('delivered_by')">
                        {{ errors.first('delivered_by') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label"
                      :class="{ required: isSale }"
                      >{{ $t('borrowingSparePart.verifiedBy') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.verified_by"
                        class="ts-w-full"
                        :options="employeeList"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                      ></a-select>
                      <div class="validate" v-if="errors.has('verified_by')">
                        {{ errors.first('verified_by') }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label"
                      :class="{ required: isSale }"
                      >{{ $t('borrowingSparePart.receivedBy') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.received_by"
                        class="ts-w-full"
                        :options="employeeList"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                      ></a-select>
                      <div class="validate" v-if="errors.has('received_by')">
                        {{ errors.first('received_by') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label class="col-md-3 form-col-label control-label">{{
                      $t('borrowingSparePart.borrowingRef')
                    }}</label>
                    <div class="col-md-7">
                      <input
                        v-model.trim="model.borrowing_ref"
                        type="text"
                        class="form-control"
                      />
                      <div class="validate" v-if="errors.has('borrowing_ref')">
                        {{ errors.first('borrowing_ref') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="ts-border ts-border-gray-300 ts-rounded-lg ts-p-3">
            <ProductDetail
              v-model="model"
              :validate="errors"
              @showSerial="onShowSerial"
              ref="detail"
            />
          </div>
          <div class="row ts-mt-4">
            <div class="col-md-5">
              <textarea
                v-model="model.remarks"
                rows="5"
                class="form-control"
                placeholder="Remark"
              ></textarea>
            </div>
            <div class="col-md-7 ts-space-x-4 ts-flex ts-justify-end ts-h-8">
              <ts-button
                @click.prevent="$router.push({ name: 'borrowing-spare-part' })"
                >{{ $t('cancel') }}</ts-button
              >
              <ts-button color="primary" @click="onUpdate">{{
                $t('update')
              }}</ts-button>
            </div>
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>
<script>
import moment from 'moment'
import ProductDetail from './components/product-details.vue'
import Customer from './components/customer.vue'
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import { includes, isEmpty } from 'lodash'
export default {
  name: 'borrowingSparePart',
  components: {
    ProductDetail,
    Customer
  },
  data() {
    return {
      loading: false,
      errors: new Errors(),
      model: {
        sfo_id: null,
        service_date: moment().format('DD-MM-YYYY'),
        customer_id: null,
        fx_rate_kh: 0,
        remarks: null,
        engine_number: null,
        requested_by: null,
        delivered_by: null,
        received_by: null,
        verified_by: null,
        borrowing_ref: null,
        is_dealer_price: false,
        is_pdf: false,
        product_detail: [],
        product_id: undefined,
        product_name: null,
        project_id: null,
        plate_number: null,
        service_type_id: null,
        service_request_id: null,
        location_id: ''
      }
    }
  },
  computed: {
    ...mapState('wholesale/borrowingSparePart', ['edit_data', 'formModels']),
    isSale() {
      return includes([3, 4], this.$store.state.authUser.user.mobile_role_id)
    },
    employeeList() {
      return this.formModels.employee.map(el => ({
        value: el.employee_id,
        label: el.employee_name_kh + '|' + el.employee_name_en
      }))
    },
    plateNumberList() {
      return this.formModels.vehiclePlate.map(el => ({
        value: el.plate_number,
        label: el.plate_number
      }))
    },
    serviceType() {
      return this.formModels.serviceType.map(el => ({
        value: el.service_type_id,
        label: el.service_type
      }))
    },
    locationList() {
      return this.formModels.location.map(el => ({
        value: el.location_id,
        label: el.location_name_en
      }))
    },
    projectList() {
      return this.formModels.project.map(el => ({
        value: el.project_id,
        label: el.project_name
      }))
    },
    isRequired() {
      return (
        this.model.product_detail.find(p => p.type_number === 2) !== undefined
      )
    }
  },
  created() {},
  methods: {
    ...mapActions('wholesale/borrowingSparePart', [
      'getFormViewData',
      'update',
      'find'
    ]),
    async init({ params, index }) {
      this.loading = true
      await this.getFormViewData({ params: params, index: index })
      this.loading = false
    },
    async setEditData() {
      await this.find(this.$route.params.id)
      await this.init({
        index:
          'customer,product,serviceType,serviceField,employee,vehiclePlate,location,project',
        params: {
          fnName:
            'customer,product,serviceType,serviceField,employee,vehiclePlate,location,project',
          search_customer: this.edit_data.customer_id,
          from_location_id: this.edit_data.location_id,
          project_id: this.edit_data.project_id
        }
      })
      if (!isEmpty(this.edit_data)) {
        this.model.sfo_id = this.edit_data.sfo_id
        this.model.service_date = this.edit_data.service_date
        this.model.customer_id = this.edit_data.customer_id
        this.model.fx_rate_kh = this.edit_data.fx_rate_khr
        this.model.remarks = this.edit_data.remarks
        this.model.engine_number = this.edit_data.engine_number
        this.model.requested_by = this.edit_data.requested_by
        this.model.delivered_by = this.edit_data.delivered_by
        this.model.received_by = this.edit_data.received_by
        this.model.verified_by = this.edit_data.verified_by
        this.model.borrowing_ref = this.edit_data.borrowing_ref
        this.model.is_dealer_price = this.edit_data.is_dealer_price
        this.model.is_pdf = this.edit_data.is_pdf
        this.model.product_id = this.edit_data.product_id
        this.model.project_id = this.edit_data.project_id
        this.model.product_name = this.edit_data.product_name_kh
        this.model.plate_number = this.edit_data.plate_number
        this.model.service_type_id = this.edit_data.service_type_id
        this.model.service_request_id = this.edit_data.service_request_id
        this.model.location_id = this.edit_data.location_id
        this.model.product_detail = []

        // this.$refs.customer_control.cus_type_id = this.edit_data.cus_type_id

        let detail = this.$refs.detail

        this.edit_data.details.forEach(el => {
          detail.tempDetails.push(el.product)

          this.model.product_detail.push({
            product_id: el.product_id,
            barcode: el.product.barcode,
            product_name: el.product.product_name_kh
              ? el.product.product_name_kh
              : el.product.product_name_en,
            uom_id: el.uom_id,
            quantity_order: el.quantity_order,
            sale_price: el.sale_price,
            vendor_standard_price: el.vendor_standard_price,
            discount_rate: el.discount_rate,
            amount: el.sale_price * el.quantity_order,
            remarks: el.remarks,
            ending_balance: el.ending_balance,
            type_number: el.type_number ? el.type_number : 2,
            over_demand: el.over_demand,
            over_demand_qty: el.over_demand_qty
          })
        })

        detail.getProductBalance()
      }
    },
    onUpdate() {
      this.loading = true
      this.update({
        id: this.model.sfo_id,
        data: this.model
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'borrowing-spare-part' })
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onShowSerial() {
      this.$refs.customer_control.show_engine = true
    },
    clearProductOnChangeLocation() {
      this.model.product_detail = []
      let detail = this.$refs.detail

      this.$nextTick(() => {
        detail.tempDetails = []
        this.$store.commit('wholesale/borrowingSparePart/SET_VIEW_MODELS', {
          data: [],
          index: 'product'
        })
      })
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.setEditData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('wholesale/borrowingSparePart/SET_EDIT_DATA', [])
    next()
  }
}
</script>
